import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Link2,
  Trash2,
} from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
  DataTableScroll,
} from "@/components/ui/DataTable";
import {
  LibraryCollection,
  LibraryCollectionTableShell,
} from "@/components/ui/LibraryCollection";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  useAdminAthlete,
  useAdminUnlinkAthlete,
} from "@/features/admin/hooks/useAdminData";
import type { AthleteAssignment, AthleteUserLink } from "@/lib/types";
import "@/features/admin/match-library.css";
import { AthleteComposerDialog } from "../components/AthleteComposerDialog";
import {
  ArchiveAthleteDialog,
  DeleteAthleteDialog,
  useRestoreAthlete,
} from "../components/AthleteLifecycleDialogs";
import { LinkAccountDialog } from "../components/LinkAccountDialog";
import { MergeAthleteCard, MergeAuditList } from "../components/MergeAthleteCard";
import {
  EndAssignmentDialog,
  RosterAssignmentDialog,
  TransferAssignmentDialog,
} from "../components/assignmentDialogs";
import { useRosterableTeams } from "../hooks/useAdminDirectoryLookups";
import {
  AssignmentStatusBadge,
  athleteActionFlags,
  AthleteAvatar,
  AthletePanelHeading,
  AthleteStatusBadge,
  formatIsoDate,
  RELATIONSHIP_LABEL,
} from "../athleteDisplay";

/**
 * `/admin/athletes/$athleteId` — one athlete in the club directory (#908,
 * plan §7): identity (photo through an assignment, D15; birth year, D11),
 * assignment history with end / transfer (§5.4, D12), linked accounts (D4)
 * and the reviewed merge with its audit (D10).
 *
 * Layout follows the People page: the admin page header carries the
 * athlete's identity and actions, then one stacked panel per section
 * (eyebrow + title, section action on the right) holding a Match
 * Library-style table.
 *
 * Rights are never guessed client-side: the links panel renders only when
 * the API set `can_manage_links`, the merge panels and the status control
 * only when it set `can_merge` (a club admin). The header mirrors the
 * directory row's quick actions: Edit (`can_edit`), Roster on a team, and
 * Archive / Restore / Delete (`can_archive`, club admin; Delete only once
 * archived, guarded by the server's `deletion` call).
 */
export function AthleteDetailPage() {
  const { athleteId } = useParams({ from: "/admin/athletes/$athleteId" });
  const navigate = useNavigate();
  const toast = useToast();
  const detailQuery = useAdminAthlete(athleteId);
  const detail = detailQuery.data;
  const athlete = detail?.athlete ?? null;
  const assignments = useMemo(() => detail?.assignments ?? [], [detail]);
  const links = useMemo(
    () => (detail?.links ?? []).filter((link) => !link.revoked_at),
    [detail],
  );
  const merges = detail?.merges ?? [];
  const canManageLinks = !!detail?.can_manage_links;
  const canMerge = !!detail?.can_merge;
  const isMerged = athlete?.status === "merged";
  const { teams, isLoading: teamsLoading } = useRosterableTeams(
    !!athlete && !isMerged,
  );
  const linkedUserIds = useMemo(
    () => new Set(links.map((link) => link.user_id)),
    [links],
  );
  const photoPlayerId =
    assignments.find((a) => a.assignment_status === "active")?.player_id ??
    assignments[0]?.player_id ??
    null;

  const flags = athlete
    ? athleteActionFlags({
        status: athlete.status,
        can_edit: detail?.can_edit,
        can_archive: detail?.can_archive,
      })
    : null;
  const { restore, isPending: restoring } = useRestoreAthlete();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [transferTarget, setTransferTarget] =
    useState<AthleteAssignment | null>(null);
  const [endTarget, setEndTarget] = useState<AthleteAssignment | null>(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<AthleteUserLink | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const unlinkMut = useAdminUnlinkAthlete();

  const revoke = async () => {
    if (!revokeTarget || !athlete) return;
    setRevokeError(null);
    try {
      await unlinkMut.mutateAsync({
        athleteId: athlete.id,
        linkId: revokeTarget.id,
      });
      toast.success("Link revoked");
      setRevokeTarget(null);
    } catch (err) {
      const message = errorMessage(err);
      setRevokeError(message);
      toast.error("Couldn't revoke the link", { description: message });
    }
  };

  const nameParts = athlete
    ? [athlete.given_name, athlete.family_name].filter(Boolean).join(" ")
    : "";

  const backLink = (
    <Link
      to="/admin/athletes"
      className="inline-flex w-fit items-center gap-1 font-mono text-[0.65rem] text-ink-muted transition-colors hover:text-ink"
      data-testid="admin-athlete-back"
    >
      <ArrowLeft size={12} aria-hidden /> Back to directory
    </Link>
  );

  return (
    <div
      data-testid="admin-athlete-detail"
      data-athlete-id={athleteId}
      className="flex flex-col gap-5"
    >
      {detailQuery.isLoading ? (
        <>
          {backLink}
          <p className="font-mono text-xs text-ink-muted">Loading athlete…</p>
        </>
      ) : detailQuery.error ? (
        <>
          {backLink}
          <p
            data-testid="admin-athlete-detail-error"
            className="font-mono text-xs text-destructive"
          >
            {errorMessage(detailQuery.error)}
          </p>
        </>
      ) : athlete ? (
        <>
          <div className="flex flex-col gap-2">
            {backLink}
            <div className="flex items-start gap-4">
              <AthleteAvatar
                name={athlete.display_name}
                playerId={photoPlayerId}
                photoFilename={athlete.photo_filename}
                className="mt-1 h-12 w-12 text-sm sm:h-14 sm:w-14 sm:text-base"
              />
              <div className="min-w-0 flex-1">
                <AdminPageHeader
                  eyebrow="Club setup · Athlete"
                  title={athlete.display_name}
                  description={
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <AthleteStatusBadge status={athlete.status} />
                      {nameParts && nameParts !== athlete.display_name ? (
                        <span data-testid="admin-athlete-full-name">
                          {nameParts}
                        </span>
                      ) : null}
                      {athlete.birth_year ? (
                        <span data-testid="admin-athlete-born">
                          Born {athlete.birth_year}
                        </span>
                      ) : null}
                      {isMerged && athlete.merged_into_athlete_id ? (
                        <span data-testid="admin-athlete-merged-into">
                          Merged into{" "}
                          <Link
                            to="/admin/athletes/$athleteId"
                            params={{ athleteId: athlete.merged_into_athlete_id }}
                            className="text-accent-text hover:underline"
                          >
                            the surviving record
                          </Link>
                          .
                        </span>
                      ) : null}
                    </span>
                  }
                  actions={
                    <>
                      {flags?.edit ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          aria-label="Edit athlete"
                          onClick={() => setEditOpen(true)}
                          data-testid="admin-athlete-edit"
                        >
                          Edit
                        </Button>
                      ) : null}
                      {flags?.archive ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setArchiveOpen(true)}
                          data-testid="admin-athlete-archive"
                        >
                          <Archive aria-hidden />
                          Archive
                        </Button>
                      ) : null}
                      {flags?.restore ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={restoring}
                          onClick={() => void restore(athlete)}
                          data-testid="admin-athlete-restore"
                        >
                          <ArchiveRestore aria-hidden />
                          Restore
                        </Button>
                      ) : null}
                      {flags?.delete ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-destructive-text"
                          onClick={() => setDeleteOpen(true)}
                          data-testid="admin-athlete-delete"
                        >
                          <Trash2 aria-hidden />
                          Delete
                        </Button>
                      ) : null}
                      {flags?.roster ? (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setRosterOpen(true)}
                          data-testid="admin-athlete-roster"
                        >
                          Roster on a team
                        </Button>
                      ) : null}
                    </>
                  }
                />
              </div>
            </div>
          </div>

          <LibraryCollection
            data-testid="admin-athlete-assignments"
            aria-labelledby="admin-athlete-assignments-title"
            className="admin-match-library-pane admin-library-pane flex flex-col gap-4"
          >
            <AthletePanelHeading
              id="admin-athlete-assignments-title"
              eyebrow="Season teams"
              title="Assignments"
            />
            <LibraryCollectionTableShell>
              <DataTableScroll>
                <DataTable className="library-collection-table">
                  <colgroup>
                    {[30, 18, 13, 21, 18].map((width, index) => (
                      <col key={index} style={{ width: `${width}%` }} />
                    ))}
                  </colgroup>
                  <DataTableHead>
                    <DataTableRow interactive={false}>
                      <DataTableHeadCell>Team & season</DataTableHeadCell>
                      <DataTableHeadCell>Jersey & position</DataTableHeadCell>
                      <DataTableHeadCell>Status</DataTableHeadCell>
                      <DataTableHeadCell>Dates</DataTableHeadCell>
                      <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                    </DataTableRow>
                  </DataTableHead>
                  <DataTableBody>
                    {assignments.length === 0 ? (
                      <DataTableEmptyRow
                        colSpan={5}
                        data-testid="admin-athlete-assignments-empty"
                      >
                        Not rostered on any team yet.
                      </DataTableEmptyRow>
                    ) : (
                      assignments.map((assignment) => (
                        <DataTableRow
                          key={assignment.player_id}
                          data-testid="admin-athlete-assignment-row"
                          data-player-id={assignment.player_id}
                          data-team-id={assignment.team_id}
                          data-assignment-status={assignment.assignment_status}
                        >
                          <DataTableCell>
                            <div className="admin-library-identity">
                              <Link
                                to="/admin/teams"
                                search={{ team: assignment.team_id }}
                                className="admin-library-title"
                              >
                                <strong>
                                  {assignment.team_name || assignment.team_id}
                                </strong>
                              </Link>
                              {assignment.season_id ? (
                                <small>
                                  {assignment.season_name || assignment.season_id}
                                </small>
                              ) : null}
                            </div>
                          </DataTableCell>
                          <DataTableCell>
                            <div className="admin-library-cell">
                              <span>
                                {assignment.jersey_number
                                  ? `#${assignment.jersey_number}`
                                  : "No jersey"}
                              </span>
                              <small>{assignment.position || "No position"}</small>
                            </div>
                          </DataTableCell>
                          <DataTableCell>
                            <AssignmentStatusBadge
                              status={assignment.assignment_status}
                            />
                          </DataTableCell>
                          <DataTableCell>
                            <div className="admin-library-cell">
                              <span>From {formatIsoDate(assignment.starts_on)}</span>
                              <small>
                                {assignment.ends_on
                                  ? `Until ${formatIsoDate(assignment.ends_on)}`
                                  : "No end date"}
                              </small>
                            </div>
                          </DataTableCell>
                          <DataTableCell align="right">
                            {assignment.assignment_status === "active" ? (
                              <div className="inline-flex flex-wrap items-center justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setTransferTarget(assignment)}
                                  data-testid="admin-athlete-assignment-transfer"
                                >
                                  Transfer
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  aria-label="End assignment"
                                  onClick={() => setEndTarget(assignment)}
                                  data-testid="admin-athlete-assignment-end"
                                >
                                  End
                                </Button>
                              </div>
                            ) : null}
                          </DataTableCell>
                        </DataTableRow>
                      ))
                    )}
                  </DataTableBody>
                </DataTable>
              </DataTableScroll>
            </LibraryCollectionTableShell>
          </LibraryCollection>

          {canManageLinks ? (
            <LibraryCollection
              data-testid="admin-athlete-links"
              aria-labelledby="admin-athlete-links-title"
              className="admin-match-library-pane admin-library-pane flex flex-col gap-4"
            >
              <AthletePanelHeading
                id="admin-athlete-links-title"
                eyebrow="Player and family accounts"
                title="Linked accounts"
                action={
                  !isMerged ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setLinkOpen(true)}
                      data-testid="admin-athlete-link-add"
                    >
                      <Link2 aria-hidden />
                      Link account
                    </Button>
                  ) : null
                }
              />
              <LibraryCollectionTableShell>
                <DataTableScroll>
                  <DataTable className="library-collection-table">
                    <colgroup>
                      {[40, 20, 22, 18].map((width, index) => (
                        <col key={index} style={{ width: `${width}%` }} />
                      ))}
                    </colgroup>
                    <DataTableHead>
                      <DataTableRow interactive={false}>
                        <DataTableHeadCell>Account</DataTableHeadCell>
                        <DataTableHeadCell>Relationship</DataTableHeadCell>
                        <DataTableHeadCell>Linked</DataTableHeadCell>
                        <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                      </DataTableRow>
                    </DataTableHead>
                    <DataTableBody>
                      {links.length === 0 ? (
                        <DataTableEmptyRow
                          colSpan={4}
                          data-testid="admin-athlete-links-empty"
                        >
                          No linked accounts.
                        </DataTableEmptyRow>
                      ) : (
                        links.map((link) => (
                          <DataTableRow
                            key={link.id}
                            data-testid="admin-athlete-link-row"
                            data-link-id={link.id}
                            data-relationship={link.relationship}
                          >
                            <DataTableCell>
                              <div className="admin-library-identity">
                                <strong>
                                  {link.display_name || link.username || link.user_id}
                                </strong>
                                {link.username ? <small>{link.username}</small> : null}
                              </div>
                            </DataTableCell>
                            <DataTableCell>
                              <Badge variant="outline">
                                {RELATIONSHIP_LABEL[link.relationship]}
                              </Badge>
                            </DataTableCell>
                            <DataTableCell>
                              <div className="admin-library-cell">
                                <span>Since {formatIsoDate(link.created_at)}</span>
                              </div>
                            </DataTableCell>
                            <DataTableCell align="right">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-label="Revoke link"
                                onClick={() => {
                                  setRevokeError(null);
                                  setRevokeTarget(link);
                                }}
                                data-testid="admin-athlete-link-revoke"
                              >
                                Revoke
                              </Button>
                            </DataTableCell>
                          </DataTableRow>
                        ))
                      )}
                    </DataTableBody>
                  </DataTable>
                </DataTableScroll>
              </LibraryCollectionTableShell>
            </LibraryCollection>
          ) : null}

          {canMerge && !isMerged ? (
            <MergeAthleteCard
              athlete={athlete}
              onMerged={(survivorAthleteId) =>
                void navigate({
                  to: "/admin/athletes/$athleteId",
                  params: { athleteId: survivorAthleteId },
                })
              }
            />
          ) : null}
          {canMerge ? <MergeAuditList athlete={athlete} merges={merges} /> : null}

          {archiveOpen ? (
            <ArchiveAthleteDialog
              athlete={athlete}
              onClose={() => setArchiveOpen(false)}
            />
          ) : null}
          {deleteOpen ? (
            <DeleteAthleteDialog
              athlete={athlete}
              onClose={() => setDeleteOpen(false)}
              onDeleted={() =>
                void navigate({ to: "/admin/athletes", replace: true })
              }
            />
          ) : null}
          {editOpen ? (
            <AthleteComposerDialog
              mode="edit"
              athlete={athlete}
              canSetStatus={canMerge}
              onClose={() => setEditOpen(false)}
              onSaved={() => setEditOpen(false)}
            />
          ) : null}
          {rosterOpen ? (
            <RosterAssignmentDialog
              athlete={athlete}
              teams={teams}
              teamsLoading={teamsLoading}
              assignments={assignments}
              onClose={() => setRosterOpen(false)}
              onSaved={() => setRosterOpen(false)}
            />
          ) : null}
          {transferTarget ? (
            <TransferAssignmentDialog
              athleteName={athlete.display_name}
              assignment={transferTarget}
              assignments={assignments}
              teams={teams}
              teamsLoading={teamsLoading}
              onClose={() => setTransferTarget(null)}
              onSaved={() => setTransferTarget(null)}
            />
          ) : null}
          {endTarget ? (
            <EndAssignmentDialog
              athleteName={athlete.display_name}
              assignment={endTarget}
              onClose={() => setEndTarget(null)}
              onEnded={() => setEndTarget(null)}
            />
          ) : null}
          {linkOpen ? (
            <LinkAccountDialog
              athlete={athlete}
              linkedUserIds={linkedUserIds}
              onClose={() => setLinkOpen(false)}
              onSaved={() => setLinkOpen(false)}
            />
          ) : null}
          {revokeTarget ? (
            <ConfirmDialog
              title="Revoke link"
              danger
              confirmDisabled={unlinkMut.isPending}
              message={
                <div className="flex flex-col gap-2 text-xs">
                  <p className="text-ink">
                    Revoke{" "}
                    <span className="font-display">
                      {revokeTarget.display_name ||
                        revokeTarget.username ||
                        revokeTarget.user_id}
                    </span>
                    &rsquo;s{" "}
                    {RELATIONSHIP_LABEL[revokeTarget.relationship].toLowerCase()}{" "}
                    link to{" "}
                    <span className="font-display">{athlete.display_name}</span>?
                  </p>
                  <p className="text-ink-muted">
                    The account loses this athlete&rsquo;s membership on every
                    team the athlete is assigned to.
                  </p>
                  {revokeError ? (
                    <p
                      data-testid="admin-athlete-link-revoke-error"
                      className="font-mono text-destructive"
                    >
                      {revokeError}
                    </p>
                  ) : null}
                </div>
              }
              confirmLabel={unlinkMut.isPending ? "Revoking…" : "Revoke link"}
              onConfirm={() => void revoke()}
              onClose={() => setRevokeTarget(null)}
              data-testid="admin-athlete-link-revoke-dialog"
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
